import { useRoutes, Navigate, Outlet } from "react-router-dom";
import Layout from "./shared/Layout/Layout";
import Dashboard from "./components/Dashboard";
import ProfilePage from "./pages/ProfilePage";
import AgentPage from "./pages/AgentPage";
import AdminPanel from "./pages/AdminPanel";
import OsMainScreen from "./pages/OsMainScreen";
import LoginPage from "./components/LoginPage";
import GuestAccessPage from "./components/GuestAccessPage";
import ExecutionHistory from "./components/ExecutionHistory";
import SettingsPage from "./components/SettingsPage";

// Blocks access if not logged in. Otherwise renders whatever child route matched.
function ProtectedRoute({ isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  return <Outlet />;
}

export function AppRoutes({
  auth,
  agents,
  loading,
  projects,
  quota,
  projectsError,
  onRetryProjects,
  onProjectsChanged,
  isAdminOrSubadmin,
  onOpenFeedback,
  navigate,
}) {
  // Wait until useAuth finishes reading localStorage before making any
  // routing decision — otherwise auth.isAuthenticated is briefly false
  // on every refresh and bounces the user to login /overview incorrectly.
  if (auth.isLoading) {
    return null;
  }
  // Guests have no project, so the project-based Overview is not theirs — their
  // home is the agent library. Everything that used to hard-code "/overview"
  // now routes through `home` so there is one answer to "where do I land".
  const isGuest = auth.user?.role === "guest";
  const home = isGuest ? "/agents" : "/overview";

  const RedirectToTerms = () => {
    window.location.replace("/saffronos-terms.html");
    return null;
  };
  const RedirectToPrivacy = () => {
    window.location.replace("/privacy-policy.html");
    return null;
  };

  const routes = [
    {
      path: "/auth/login",
      element: auth.isAuthenticated ? (
        <Navigate to={home} replace />
      ) : (
        <LoginPage auth={auth} />
      ),
    },
    {
      // Private guest invitation. PUBLIC on purpose: this screen is how a guest
      // signs in, so it must resolve while unauthenticated. Without it the
      // catch-all below swallowed /access/<token> and bounced it to the login
      // form, which is exactly what it looked like from the outside.
      path: "/access/:token",
      element: auth.isAuthenticated ? (
        <Navigate to={home} replace />
      ) : (
        <GuestAccessPage auth={auth} />
      ),
    },
    {
      // Admin — checks login FIRST, then role
      element: <ProtectedRoute isAuthenticated={auth.isAuthenticated} />,
      children: [
        {
          path: "/admin/*",
          element: isAdminOrSubadmin ? (
            <AdminPanel
              currentUser={auth.user}
              onBackToApp={() => navigate("/")}
            />
          ) : (
            <Navigate to="/" replace />
          ),
        },
      ],
    },
    {
      // Main app — protected as a whole
      element: <ProtectedRoute isAuthenticated={auth.isAuthenticated} />,
      children: [
        {
          // Parent route — no "path", just element + children.
          // Layout renders once; children render into its <Outlet />.
          element: (
            <Layout
              auth={auth}
              agents={agents}
              projects={projects}
              quota={quota}
              projectsError={projectsError}
              onRetryProjects={onRetryProjects}
              onProjectsChanged={onProjectsChanged}
              isAdminOrSubadmin={isAdminOrSubadmin}
              onOpenFeedback={onOpenFeedback}
            />
          ),
          children: [
            { path: "/", element: <Navigate to={home} replace /> },
            {
              path: "/overview",
              // Reads a project's score history, which a guest does not have.
              element: isGuest ? (
                <Navigate to="/agents" replace />
              ) : (
                <OsMainScreen
                  loading={loading}
                  projects={projects}
                  quota={quota}
                />
              ),
            },
            {
              path: "/agents",
              element: <Dashboard agents={agents} loading={loading} />,
            },
            {
              path: "/agent-library",
              element: <Dashboard agents={agents} loading={loading} />,
            },
            {
              path: "/history",
              element: (
                <ExecutionHistory
                  projects={projects}
                  onOpenExecution={(execution) =>
                    navigate(
                      `/agent/${execution.agentId}?executionId=${execution.id}&from=history`,
                    )
                  }
                />
              ),
            },
            {
              // Own page now — no longer routed through Dashboard's tab logic.
              path: "/settings",
              element: <SettingsPage />,
            },
            { path: "/profile", element: <ProfilePage auth={auth} /> },
            { path: "/agent/:agentId", element: <AgentPage /> },
          ],
        },
      ],
    },
    { path: "/terms-of-service", element: <RedirectToTerms /> },
    { path: "/privacy-policy", element: <RedirectToPrivacy /> },
    {
      path: "*",
      element: auth.isAuthenticated ? (
        <Navigate to={home} replace />
      ) : (
        <Navigate to="/auth/login" replace />
      ),
    },
  ];

  return useRoutes(routes);
}
